import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { differenceInMilliseconds } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDeleteNavModal } from '@/modules/issuer-dashboard/components/modals/IssuerDeleteNavModal.tsx';
import { IssuerEditNavModal } from '@/modules/issuer-dashboard/components/modals/IssuerEditNavModal.tsx';
import { IssuerAddNavModal } from '@/modules/issuer-dashboard/components/nav-managment/IssuerAddNavModal.tsx';
import { useIssuerNavListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  Badge,
  BodyCell,
  Button,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  Tooltip,
  TooltipNew,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { AlertOrangeIcon, CloseIcon, EditIcon, SeedingIcon, TrashGrayIcon } from 'ui/icons';
import { goodFormatUSDCurrency } from 'ui/utils';
import { IssuerRegisterClassResponse, NAVHistoryItemResponse } from 'api';

export interface IssuerNavInfoModalProps extends Partial<IssuerRegisterClassResponse> {
  register_class_id: number;
  open: boolean;
  onClose: () => void;
}

type TypeHeadNav = 'Active From' | 'Created At' | 'Price Per Share/Unit' | '';

const headItems: TableHeadColumn<TypeHeadNav>[] = [
  {
    title: 'Active From',
    className: 'font-normal min-w-[150px] pl-4',
  },
  {
    title: 'Created At',
    className: 'font-normal min-w-[150px]',
  },
  {
    title: 'Price Per Share/Unit',
    className: 'font-normal min-w-[150px]',
  },
  { title: '' },
];

export const IssuerNavInfoModal = ({
  open,
  onClose,
  register_class_id,
  register_obj,
  class_name,
}: IssuerNavInfoModalProps) => {
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data: navList } = useIssuerNavListQuery(
    {
      page: 1,
      page_size: 20,
      register_class_id: register_class_id ?? 0,
    },
    !register_class_id,
  );

  const activeNav = useMemo(
    () => navList?.pages?.flatMap(page => page.data).find(item => item.is_active === true),
    [navList?.pages],
  );

  const handleAdd = () => {
    setIsOpenAdd(isOpenAdd => !isOpenAdd);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog className="relative z-[2] right-0" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 right-0">
          <div className="flex h-screen w-full items-center justify-end text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full h-full max-w-2xl min-w-[300px] space-y-[18px] transform bg-white text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="w-full flex items-start pt-[18px] px-6 justify-between sm:flex-row"
                >
                  <div className="flex items-start space-x-3 w-3/5 sm:w-2/3">
                    <p
                      className="text-[18px] leading-[22px] font-serif font-semibold	break-words w-full max-w-[450px]"
                      style={{ wordBreak: 'break-word' }}
                    >
                      <span className="block sm:text-[20px] sm:leading-[28px]">
                        {register_obj?.name ?? ''} {class_name} {getRegisterTypeStrings('NAV')}{' '}
                        history
                      </span>
                    </p>
                  </div>
                  <div className="flex mt-0">
                    <Button className="min-w-[96px] mr-3" onClick={handleAdd}>
                      Update {getRegisterTypeStrings('NAV')}
                    </Button>
                    <IssuerAddNavModal
                      onClose={handleAdd}
                      open={isOpenAdd}
                      register_class_id={register_class_id}
                      activeDate={new Date(activeNav?.changes_date ?? new Date())}
                    />
                    <button onClick={onClose}>
                      <img src={CloseIcon} alt="Close" />
                    </button>
                  </div>
                </Dialog.Title>
                <div className="w-full h-5/6 overflow-y-auto pb-4" id="block-navs-list">
                  <NavInfoTable
                    register_class_id={register_class_id}
                    inModal={true}
                    classNameHead="border-t-none"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

interface NavInfoTableProps {
  register_class_id?: number;
  limit?: number;
  createdDates?: [Dayjs | null, Dayjs | null] | null;
  activeDates?: [Dayjs | null, Dayjs | null] | null;
  blockIdInfinite?: string;
  classNameHead?: string;
  className?: string;
  withBorder?: boolean;
  inModal?: boolean;
}

export const NavInfoTable = ({
  register_class_id,
  blockIdInfinite = 'block-navs-list',
  limit = 50,
  activeDates,
  createdDates,
  className = 'my-0 border-t rounded-b-xl',
  classNameHead,
  withBorder,
  inModal = false,
}: NavInfoTableProps) => {
  const { isMobile } = useIsMobile();
  const {
    hasNextPage,
    fetchNextPage,
    data: navList,
    isLoading,
    isError,
  } = useIssuerNavListQuery(
    {
      page: 1,
      page_size: limit,
      register_class_id: register_class_id ?? 0,
      active_from: activeDates?.[0] ? dayjs(activeDates[0]).format('YYYY-MM-DD') : undefined,
      active_to: activeDates?.[1] ? dayjs(activeDates[1]).format('YYYY-MM-DD') : undefined,
      created_at_from: createdDates?.[0] ? dayjs(createdDates[0]).format('YYYY-MM-DD') : undefined,
      created_at_to: createdDates?.[1] ? dayjs(createdDates[1]).format('YYYY-MM-DD') : undefined,
    },
    !register_class_id,
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!navList?.pages) {
      return [];
    }

    const activeNav = navList.pages
      .flatMap(page => page.data)
      .find(item => item.is_active === true);
    const activeNavDate = new Date(activeNav?.changes_date ?? new Date());
    const result: TableBodyColumn[] = [];

    navList.pages.forEach(page => {
      page?.data?.forEach((data, i, all) => {
        const body: BodyCell[] = [
          {
            cell: (
              <span className="flex items-center gap-4">
                <span>{dateToPrint(data.changes_date, DateVariant.Table, false) || '-'}</span>
                {data.is_from_portfolio && (
                  <span>
                    <TooltipNew text="From Custody portfolios">
                      <img src={SeedingIcon} alt="From Custody portfolios" />
                    </TooltipNew>
                  </span>
                )}
                {data.is_from_portfolio_edited && (
                  <span>
                    <TooltipNew text="Calculated based by Custody portfolios, but manually edited">
                      <Badge className="bg-[#c38200] text-white">Edited</Badge>
                    </TooltipNew>
                  </span>
                )}
                {data.is_active && (
                  <span>
                    <Badge className="bg-[#0369A1] text-white">Actual</Badge>
                  </span>
                )}
              </span>
            ),
            className: 'pl-4',
          },
          {
            cell: <>{dateToPrint(data.created_at, DateVariant.Table) || '-'}</>,
          },
          {
            cell: goodFormatUSDCurrency(data.price_per_share),
            className: 'text-right w-[185px]',
          },
          {
            cell: (
              <MoreButtonsNav
                i={i}
                all={all}
                data={data}
                activeNavDate={activeNavDate}
                className={inModal ? 'gap-2' : ''}
              />
            ),
            className: 'pr-4',
          },
        ];

        result.push({
          cells: body,
        });
      });
    });

    return result;
  }, [inModal, navList]);

  return (
    <Table
      classNameHead={classNameHead}
      className={className}
      withRounded={isMobile}
      withBorderX={false}
      withBorder={isMobile || withBorder}
      headItems={headItems}
      items={bodyItems}
      isLoading={isLoading}
      isError={isError}
      hasNextPageInfinite={hasNextPage}
      fetchNextPageInfinite={fetchNextPage}
      blockIdInfinite={blockIdInfinite}
      withQueryParams={false}
    />
  );
};

interface MoreButtonsNavProps {
  all: NAVHistoryItemResponse[];
  i: number;
  data: NAVHistoryItemResponse;
  activeNavDate: Date;
  className?: string;
}

export const MoreButtonsNav = ({ all, i, data, activeNavDate, className }: MoreButtonsNavProps) => {
  const [openEditId, setOpenEditId] = useState<number | null>(null);
  const [openDeleteId, setOpenDeleteId] = useState<number | null>(null);
  const isActualAndHistoricalPrice =
    differenceInMilliseconds(activeNavDate, new Date(data.changes_date)) >= 0;

  const handleEdit = useCallback(
    (id: number) => {
      setOpenEditId(openEditId === id ? null : id);
    },
    [openEditId],
  );

  const handleDelete = useCallback(
    (id: number) => {
      setOpenDeleteId(openDeleteId === id ? null : id);
    },
    [openDeleteId],
  );

  return (
    <div
      className={classNames(
        'flex justify-end',
        {
          'gap-8': !className?.includes('gap-'),
        },
        className,
      )}
    >
      {isActualAndHistoricalPrice && (
        <Tooltip
          classNameTooltip="border"
          positionHorizontal="left"
          position={i === 0 || all.length - 1 === i ? 'up' : 'center'}
          variant="gray"
          button={<img src={AlertOrangeIcon} alt="warning" className="w-6 h-6 min-w-6 min-h-6" />}
        >
          Deleting historical NAVs requires justification
        </Tooltip>
      )}

      <button onClick={() => handleDelete(data.id)} className="cursor-pointer hover:opacity-70">
        <img src={TrashGrayIcon} alt="Remove nav" className="w-6 h-6 min-w-6 min-h-6" />
      </button>

      <button onClick={() => handleEdit(data.id)} className="cursor-pointer hover:opacity-70">
        <img src={EditIcon} alt="Edit nav" className="w-6 h-6 min-w-[24px] min-h-[24px]" />
      </button>
      <IssuerEditNavModal
        isActualAndHistoricalPrice={isActualAndHistoricalPrice}
        open={openEditId === data.id}
        onClose={() => handleEdit(data.id)}
        changes_date={data.changes_date}
        id={data.id}
        price_per_share={data.price_per_share}
      />
      <IssuerDeleteNavModal
        isActualAndHistoricalPrice={isActualAndHistoricalPrice}
        open={openDeleteId === data.id}
        onClose={() => handleDelete(data.id)}
        id={data.id}
      />
    </div>
  );
};

// classNameHead="bg-transparent"
// className="my-0 rounded-b-xl"
